import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Route, Switch, useRouteMatch} from 'react-router-dom';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Container from '../common/layout/Container';
import EditUserAccount from './EditUserAccount';
import useFetch from '../../hooks/useFetch';
import Error from '../common/Error';
import Tabs from '../common/Tabs';
import {UserAccountStateProps, MatchParams} from '../../constants/types';
import permissionsInfo from '../../constants/permissionsInfo';
import Spinner from '../common/Spinner';
import AddToTenant from '../common/tenants/AddToTenant';
import AddToGroup from '../common/groups/AddToGroup';
import {editUserData} from '../../constants/userData';
import routes from './routes';
import {DEFAULT_ERROR} from '../../api/constants';

enum TabPath {
  GROUPS = '/groups',
  TENANTS = '/tenants'
}

const getTabs = (path: string, tabLabels) => {
  const isSysConfPath = path.includes(routes.users());

  if (isSysConfPath) {
    return [
      {
        name: tabLabels.account,
        link: path,
        permissions: permissionsInfo.users.view
      },
      {
        name: tabLabels.tenants,
        link: `${path}${TabPath.TENANTS}`,
        permissions: permissionsInfo.users.manage.groups
      },
      {
        name: tabLabels.groups,
        link: `${path}${TabPath.GROUPS}`,
        permissions: permissionsInfo.users.manage.groups
      }
    ];
  }

  return [
    {
      name: tabLabels.account,
      link: path,
      permissions: permissionsInfo.tenants_config.users.view
    },
    {
      name: tabLabels.groups,
      link: `${path}${TabPath.GROUPS}`,
      permissions: permissionsInfo.tenants.manage.groups
    }
  ];
};

const EditUser = () => {
  const {t} = useTranslation('user');
  const dispatch = useAppDispatch();
  const {url, params, path}: MatchParams = useRouteMatch();
  const {id} = params || {};
  const [userData, setUserData] =
    useState<Nullable<UserAccountStateProps>>(null);

  const tabLabels = {
    account: t('userDetails.tabs.account.label'),
    groups: t('userDetails.tabs.groups.label'),
    tenants: t('userDetails.tabs.tenants.label')
  };

  const entity = editUserData?.[path];
  const [data, isLoading, error] = useFetch(entity.fetchUrl(id));

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  useEffect(() => {
    if (data) setUserData(data);
  }, [data]);

  if (error) {
    return <Error message={error?.message || DEFAULT_ERROR} />;
  }

  const {
    firstName,
    lastName,
    email,
    locked
  } = userData || {};
  const fullName = firstName && lastName ? [firstName, lastName].join(' ') : '';

  return (
    <Container header={entity?.header}>
      {!isLoading ? (
        <>
          <h2 className="subheader">
            {t('userDetails.subheader')}
            {fullName && ` - ${fullName}`}
          </h2>
          <Tabs tabs={getTabs(url, tabLabels)} />

          <Switch>
            <Route exact path={url}>
              <EditUserAccount
                id={id}
                firstName={firstName || ''}
                lastName={lastName || ''}
                email={email || ''}
                locked={locked || false}
                entity={entity}
              />
            </Route>

            <Route exact path={`${url}${TabPath.GROUPS}`}>
              <AddToGroup
                id={id}
                name={fullName}
                entityKey={`${path}${TabPath.GROUPS}`}
              />
            </Route>

            <Route exact path={`${url}${TabPath.TENANTS}`}>
              <AddToTenant
                id={id}
                name={fullName}
                entityKey={`${path}${TabPath.TENANTS}`}
                toastTitle={t('toasts.toastTitle')}
              />
            </Route>
          </Switch>
        </>) :
        (<Spinner loadingText={t('userDetails.loadingText')} />)}
    </Container>
  );
};

export default EditUser;
